import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Building2,
  MapPin,
  Users,
  FileText,
  Plus,
  CheckCircle,
  XCircle,
  PauseCircle,
  PlayCircle,
  ExternalLink,
  Phone,
  Mail,
  Hash,
  Calendar,
  Info,
  Wallet,
  Pencil,
  Package,
  Trash2,
  TrendingUp,
} from 'lucide-react';
import { businessesService } from '@services/businesses.service';
import { writesApi } from '@services/api';
import { PermissionGate } from '@components/PermissionGate';
import { ProductDetailModal } from '@components/businesses/ProductDetailModal';
import { StaffDetailModal } from '@components/businesses/StaffDetailModal';
import { BusinessPayoutPanel } from '@components/businesses/BusinessPayoutPanel';
import { BusinessEditModal } from '@components/businesses/BusinessEditModal';
import { BusinessOnboardingPanel } from '@components/businesses/BusinessOnboardingPanel';
import { cn } from '@lib/utils';

// ── Status colours ─────────────────────────────────────────────────────────
const bizStatusColors: Record<string, string> = {
  active: 'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  pending_review: 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
  suspended: 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  deactivated: 'bg-[var(--bg-base)] text-[var(--text-muted)]',
};

const kycStatusColors: Record<string, string> = {
  pending: 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
  approved: 'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  rejected: 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  under_review: 'bg-[var(--bg-elevated)] text-[var(--text-accent)]',
};

// §11c: seniority is a magnitude, so it rides the intensity ramp. `manager` was
// AMBER, i.e. --state-warning. A manager is not a warning. Semantic states
// signal; they never decorate (§11b), and a rank is not a state.
//
// `owner` KEEPS its red on purpose, and it is the licensed exception: there the
// colour is not decorating a rank, it is a live warning that this account holds
// irreversible, destructive permissions. The test is whether the colour is
// telling the operator to be careful. For `owner` it is. For `manager` it was
// not.
//
// `chekit_assigned` sits off the ramp deliberately: it is not a rung on this
// ladder, it is a different axis (Chekit staff placed inside a business).
const staffTypeBadge: Record<string, string> = {
  owner: 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  admin: 'bg-[var(--progress-3)] text-[var(--progress-3-fg)]',
  manager: 'bg-[var(--progress-2)] text-[var(--progress-2-fg)]',
  staff: 'bg-[var(--bg-base)] text-[var(--text-muted)]',
  chekit_assigned: 'bg-[var(--bg-elevated)] text-[var(--text-accent)]',
};

// ── Helper: avatar initials ─────────────────────────────────────────────────
function initials(first?: string, last?: string): string {
  return `${(first?.[0] ?? '').toUpperCase()}${(last?.[0] ?? '').toUpperCase()}` || '??';
}

// ── Confirm-with-reason modal ───────────────────────────────────────────────
interface ReasonModalProps {
  title: string;
  description: string;
  confirmLabel: string;
  confirmClass: string;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  isPending: boolean;
}

const ReasonModal = ({
  title,
  description,
  confirmLabel,
  confirmClass,
  onConfirm,
  onCancel,
  isPending,
}: ReasonModalProps) => {
  const [reason, setReason] = useState('');

  return (
    <div
      className="fixed inset-0 bg-[rgba(15,61,30,0.50)] z-50 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-3xl shadow-lift p-8 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)] mb-1">{title}</h3>
        <p className="text-[14px] text-[var(--text-muted)] mb-5">{description}</p>
        <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">
          Reason <span className="text-[var(--state-danger)]">*</span>
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Enter a reason..."
          rows={3}
          className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200 resize-none mb-5"
        />
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-[var(--border-subtle)] bg-white px-5 py-2.5 text-[14px] font-semibold text-[var(--text-primary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)] transition-all duration-200"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!reason.trim() || isPending}
            onClick={() => onConfirm(reason.trim())}
            className={cn(
              'flex-1 inline-flex items-center justify-center rounded-full px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed',
              confirmClass,
            )}
          >
            {isPending ? 'Please wait...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── CreateBranchModal ───────────────────────────────────────────────────────
interface CreateBranchModalProps {
  businessId: string;
  onClose: () => void;
}

const CreateBranchModal = ({ businessId, onClose }: CreateBranchModalProps) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    addressLine1: '',
    city: '',
  });

  const mutation = useMutation({
    mutationFn: () =>
      businessesService.createBranch(businessId, {
        name: form.name,
        phone: form.phone || undefined,
        email: form.email || undefined,
        addressLine1: form.addressLine1 || undefined,
        city: form.city || undefined,
      } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-branches', businessId] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <div
      className="fixed inset-0 bg-[rgba(15,61,30,0.50)] z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-lift p-8 max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-[15px] font-semibold text-[var(--text-primary)] mb-5">Add Branch</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">
              Name <span className="text-[var(--state-danger)]">*</span>
            </label>
            <input
              required
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Branch name"
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">Phone</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+254..."
                className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200"
              />
            </div>
            <div>
              <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="branch@example.com"
                className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200"
              />
            </div>
          </div>
          <div>
            <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">Address Line 1</label>
            <input
              type="text"
              value={form.addressLine1}
              onChange={(e) => setForm({ ...form, addressLine1: e.target.value })}
              placeholder="Street address"
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200"
            />
          </div>
          <div>
            <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">City</label>
            <input
              type="text"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              placeholder="Nairobi"
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200"
            />
          </div>
          {mutation.isError && (
            <p className="text-sm text-[var(--state-danger)]">Failed to create branch. Please try again.</p>
          )}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-[var(--border-subtle)] bg-white px-5 py-2.5 text-[14px] font-semibold text-[var(--text-primary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)] transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
            >
              {mutation.isPending ? 'Creating...' : 'Create Branch'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── AddStaffModal ───────────────────────────────────────────────────────────
interface AddStaffModalProps {
  businessId: string;
  onClose: () => void;
}

const AddStaffModal = ({ businessId, onClose }: AddStaffModalProps) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    phone: '',
    firstName: '',
    lastName: '',
    email: '',
    nationalId: '',
    dateOfBirth: '',
    gender: '',
    staffType: 'staff',
    jobTitle: '',
  });

  const mutation = useMutation({
    mutationFn: () =>
      businessesService.addStaff(businessId, {
        phone: form.phone.trim(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        staffType: form.staffType,
        email: form.email.trim() || undefined,
        nationalId: form.nationalId.trim() || undefined,
        dateOfBirth: form.dateOfBirth || undefined,
        gender: form.gender || undefined,
        jobTitle: form.jobTitle.trim() || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-staff', businessId] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  const inputCls =
    'w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200';
  const labelCls = 'block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2';

  return (
    <div
      className="fixed inset-0 bg-[rgba(15,61,30,0.50)] z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-lift max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-8 pt-8 pb-2">
          <h3 className="font-display text-[24px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">Add Staff Member</h3>
          <p className="text-[13px] text-[var(--text-muted)] mt-1">
            Profile fields below are used only when the phone has no account yet.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="px-8 pb-8 pt-4 space-y-4 overflow-y-auto">
          {/* Identity */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>First Name <span className="text-[var(--state-danger)]">*</span></label>
              <input
                required
                type="text"
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Last Name <span className="text-[var(--state-danger)]">*</span></label>
              <input
                required
                type="text"
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                className={inputCls}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Phone Number <span className="text-[var(--state-danger)]">*</span></label>
              <input
                required
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+254..."
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="staff@example.com"
                className={inputCls}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>National ID</label>
              <input
                type="text"
                value={form.nationalId}
                onChange={(e) => setForm({ ...form, nationalId: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Date of Birth</label>
              <input
                type="date"
                value={form.dateOfBirth}
                onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Gender</label>
              <select
                value={form.gender}
                onChange={(e) => setForm({ ...form, gender: e.target.value })}
                className={inputCls}
              >
                <option value="">—</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
                <option value="prefer_not_to_say">Prefer not to say</option>
              </select>
            </div>
          </div>

          {/* Role */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Role <span className="text-[var(--state-danger)]">*</span></label>
              <select
                value={form.staffType}
                onChange={(e) => setForm({ ...form, staffType: e.target.value })}
                className={inputCls}
              >
                <option value="owner">Owner</option>
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
                <option value="staff">Staff</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Job Title</label>
              <input
                type="text"
                value={form.jobTitle}
                onChange={(e) => setForm({ ...form, jobTitle: e.target.value })}
                placeholder="e.g. Branch Manager"
                className={inputCls}
              />
            </div>
          </div>

          {mutation.isError && (
            <div className="rounded-2xl border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] px-4 py-3">
              <p className="text-[13px] text-[var(--state-danger)]">
                {(mutation.error as { message?: string })?.message ?? 'Failed to add staff member.'}
              </p>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-[var(--border-subtle)] bg-white px-5 py-2.5 text-[14px] font-semibold text-[var(--text-primary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)] transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
            >
              {mutation.isPending ? 'Adding...' : 'Add Staff'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Detail row helper ───────────────────────────────────────────────────────
const DetailRow = ({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value?: string | number | null;
}) => (
  <div className="flex items-start gap-3 py-3 border-b border-[var(--border-subtle)] last:border-0">
    <Icon className="w-4 h-4 text-[var(--text-muted)] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
    <div className="flex-1 min-w-0">
      <p className="text-[13px] text-[var(--text-muted)] mb-0.5">{label}</p>
      <p className="text-[14px] font-medium text-[var(--text-primary)] break-words">
        {value ?? <span className="text-[var(--text-muted)]">—</span>}
      </p>
    </div>
  </div>
);

// ── Format KES (NUMERIC(15,2), not cents) ────────────────────────────────────
const formatKes = (value: number, currency = 'KES') =>
  `${currency} ${value.toLocaleString('en-KE', { minimumFractionDigits: 2 })}`;

// ── Main page ───────────────────────────────────────────────────────────────
type Tab = 'overview' | 'branches' | 'staff' | 'kyc' | 'finance' | 'products';

export const BusinessDetailPage = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [rejectModal, setRejectModal] = useState(false);
  const [suspendModal, setSuspendModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [showCreateBranch, setShowCreateBranch] = useState(false);
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [kycRejectDocId, setKycRejectDocId] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [selectedStaff, setSelectedStaff] = useState<any | null>(null);

  // ── Queries ───────────────────────────────────────────────────────────────
  const { data: business, isLoading, error } = useQuery({
    queryKey: ['business', businessId],
    queryFn: () => businessesService.get(businessId!),
    enabled: !!businessId,
  });

  const { data: branches, isLoading: branchesLoading } = useQuery({
    queryKey: ['business-branches', businessId],
    queryFn: () => businessesService.listBranches(businessId!),
    enabled: !!businessId && activeTab === 'branches',
  });

  const { data: staff, isLoading: staffLoading } = useQuery({
    queryKey: ['business-staff', businessId],
    queryFn: () => businessesService.listStaff(businessId!),
    enabled: !!businessId && activeTab === 'staff',
  });

  const { data: kycDocs, isLoading: kycLoading } = useQuery({
    queryKey: ['business-kyc', businessId],
    queryFn: () => businessesService.getKyc(businessId!),
    enabled: !!businessId && activeTab === 'kyc',
  });

  const { data: wallets = [], isLoading: walletsLoading } = useQuery({
    queryKey: ['business-wallets', businessId],
    queryFn: () => businessesService.getWallets(businessId!),
    enabled: !!businessId && activeTab === 'finance',
  });

  const [productPage, setProductPage] = useState(1);
  const [productSearchInput, setProductSearchInput] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const PRODUCT_PAGE_SIZE = 20;

  // Debounce the search box; any new search restarts at page 1.
  useEffect(() => {
    const t = setTimeout(() => {
      setProductSearch(productSearchInput.trim());
      setProductPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [productSearchInput]);

  const { data: productsPage, isLoading: productsLoading, isFetching: productsFetching } = useQuery({
    queryKey: ['business-products', businessId, productPage, productSearch],
    queryFn: () =>
      businessesService.listProducts(businessId!, {
        page: productPage,
        limit: PRODUCT_PAGE_SIZE,
        search: productSearch || undefined,
      }),
    enabled: !!businessId && activeTab === 'products',
    placeholderData: (prev) => prev,
  });
  const products = productsPage?.data ?? [];
  const productsTotal = productsPage?.total ?? 0;
  const productPageCount = Math.max(1, Math.ceil(productsTotal / PRODUCT_PAGE_SIZE));

  const [productActionError, setProductActionError] = useState<string | null>(null);

  const onProductActionSuccess = () => {
    setProductActionError(null);
    queryClient.invalidateQueries({ queryKey: ['business-products', businessId] });
  };

  const onProductActionError = (err: unknown) => {
    const msg = (err as { message?: string })?.message ?? 'Action failed. Please try again.';
    setProductActionError(msg);
  };

  const suspendProductMutation = useMutation({
    mutationFn: (productId: string) => businessesService.suspendProduct(businessId!, productId),
    onSuccess: onProductActionSuccess,
    onError: onProductActionError,
  });

  const activateProductMutation = useMutation({
    mutationFn: (productId: string) => businessesService.activateProduct(businessId!, productId),
    onSuccess: onProductActionSuccess,
    onError: onProductActionError,
  });

  const removeProductMutation = useMutation({
    mutationFn: (productId: string) => businessesService.removeProduct(businessId!, productId),
    onSuccess: onProductActionSuccess,
    onError: onProductActionError,
  });

  // Keep the open modal in sync with the refetched products list so saves
  // are reflected immediately in the detail view.
  useEffect(() => {
    if (!selectedProduct || !Array.isArray(products)) return;
    const fresh = (products as any[]).find((p) => p.id === selectedProduct.id);
    if (fresh && fresh !== selectedProduct) {
      setSelectedProduct(fresh);
    }
  }, [products, selectedProduct]);

  // Same sync for the staff modal.
  useEffect(() => {
    if (!selectedStaff || !Array.isArray(staff)) return;
    const fresh = (staff as any[]).find((s) => s.id === selectedStaff.id);
    if (fresh && fresh !== selectedStaff) {
      setSelectedStaff(fresh);
    }
  }, [staff, selectedStaff]);

  // ── Business status mutations ─────────────────────────────────────────────
  const invalidateBiz = () =>
    queryClient.invalidateQueries({ queryKey: ['business', businessId] });

  const approveMutation = useMutation({
    mutationFn: () => businessesService.approve(businessId!),
    onSuccess: invalidateBiz,
  });

  const rejectMutation = useMutation({
    mutationFn: (reason: string) => businessesService.reject(businessId!, reason),
    onSuccess: () => { setRejectModal(false); invalidateBiz(); },
  });

  const suspendMutation = useMutation({
    mutationFn: (reason: string) => businessesService.suspend(businessId!, reason),
    onSuccess: () => { setSuspendModal(false); invalidateBiz(); },
  });

  const activateMutation = useMutation({
    mutationFn: () => businessesService.activate(businessId!),
    onSuccess: invalidateBiz,
  });

  // ── KYC mutations ─────────────────────────────────────────────────────────
  const kycApproveMutation = useMutation({
    mutationFn: (docId: string) => writesApi.post(`/admin/kyc/${docId}/approve`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['business-kyc', businessId] }),
  });

  const kycRejectMutation = useMutation({
    mutationFn: ({ docId, reason }: { docId: string; reason: string }) =>
      writesApi.post(`/admin/kyc/${docId}/reject`, { reason }),
    onSuccess: () => {
      setKycRejectDocId(null);
      queryClient.invalidateQueries({ queryKey: ['business-kyc', businessId] });
    },
  });

  // ── Loading / error states ────────────────────────────────────────────────
  if (isLoading) {
    return <div className="p-12 text-center text-[var(--text-muted)]">Loading business...</div>;
  }
  if (error || !business) {
    return <div className="p-12 text-center text-[var(--state-danger)]">Business not found or failed to load.</div>;
  }

  const tabs: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: 'overview', label: 'Overview', icon: Info },
    { id: 'branches', label: 'Branches', icon: MapPin },
    { id: 'staff', label: 'Staff', icon: Users },
    { id: 'kyc', label: 'KYC', icon: FileText },
    { id: 'finance', label: 'Finance', icon: Wallet },
    { id: 'products', label: 'Products', icon: Package },
  ];

  return (
    <div className="space-y-6">
      {/* Back */}
      <Link
        to="/businesses"
        className="inline-flex items-center gap-2 text-[14px] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors duration-200"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        Back to Businesses
      </Link>

      {/* ── Business Header ─────────────────────────────────────────────── */}
      <div className="rounded-3xl bg-white shadow-soft p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          {/* Identity */}
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[var(--bg-elevated)] text-[var(--text-accent)] rounded-2xl flex items-center justify-center flex-shrink-0">
              <Building2 className="w-7 h-7" strokeWidth={1.5} />
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">{business.name}</h2>
                <span
                  className={cn(
                    'rounded-full px-3 py-1 text-[12px] font-semibold capitalize',
                    bizStatusColors[business.status] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                  )}
                >
                  {business.status?.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-1 text-[13px] text-[var(--text-muted)] flex-wrap">
                <span className="flex items-center gap-1">
                  <Hash className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {business.businessCode ?? '—'}
                </span>
                {business.taxType && (
                  <span className="flex items-center gap-1">Tax: {business.taxType}</span>
                )}
                {business.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {business.phone}
                  </span>
                )}
                {business.email && (
                  <span className="flex items-center gap-1">
                    <Mail className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {business.email}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 flex-wrap">
            {business.status === 'pending_review' && (
              <>
                <PermissionGate permission="businesses.approve">
                  <button
                    onClick={() => approveMutation.mutate()}
                    disabled={approveMutation.isPending}
                    className="inline-flex items-center gap-2 rounded-full bg-[var(--gradient-to)] px-4 py-2 text-[13px] font-semibold text-white transition-all duration-200 disabled:opacity-40 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
                  >
                    <CheckCircle className="w-4 h-4" strokeWidth={1.5} />
                    {approveMutation.isPending ? 'Approving...' : 'Approve'}
                  </button>
                </PermissionGate>
                <PermissionGate permission="businesses.reject">
                  <button
                    onClick={() => setRejectModal(true)}
                    className="inline-flex items-center gap-2 rounded-full border border-[var(--state-danger-border)] text-[var(--state-danger)] px-4 py-2 text-[13px] font-semibold hover:bg-[var(--state-danger-bg)] transition-all duration-200"
                  >
                    <XCircle className="w-4 h-4" strokeWidth={1.5} />
                    Reject
                  </button>
                </PermissionGate>
              </>
            )}

            <PermissionGate permission="businesses.update">
              <button
                onClick={() => setEditModal(true)}
                className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] text-[var(--text-primary)] px-4 py-2 text-[13px] font-semibold hover:bg-[var(--bg-base)] transition-all duration-200"
              >
                <Pencil className="w-4 h-4" strokeWidth={1.5} />
                Edit
              </button>
            </PermissionGate>

            {business.status === 'active' && (
              <PermissionGate permission="businesses.suspend">
                <button
                  onClick={() => setSuspendModal(true)}
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--state-warning-border)] text-[var(--state-warning)] px-4 py-2 text-[13px] font-semibold hover:bg-[var(--state-warning-bg)] transition-all duration-200"
                >
                  <PauseCircle className="w-4 h-4" strokeWidth={1.5} />
                  Suspend
                </button>
              </PermissionGate>
            )}

            {business.status === 'suspended' && (
              <PermissionGate permission="businesses.approve">
                <button
                  onClick={() => activateMutation.mutate()}
                  disabled={activateMutation.isPending}
                  className="inline-flex items-center gap-2 rounded-full bg-[var(--gradient-to)] px-4 py-2 text-[13px] font-semibold text-white transition-all duration-200 disabled:opacity-40 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
                >
                  <PlayCircle className="w-4 h-4" strokeWidth={1.5} />
                  {activateMutation.isPending ? 'Activating...' : 'Activate'}
                </button>
              </PermissionGate>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-6 border-b border-[var(--border-subtle)]">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={cn(
                'flex items-center gap-2 px-4 py-3 text-[13px] font-medium border-b-2 -mb-px transition-all duration-200',
                activeTab === id
                  ? 'border-[var(--border-strong)] text-[var(--text-primary)]'
                  : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]',
              )}
            >
              <Icon className="w-4 h-4" strokeWidth={1.5} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Overview tab ────────────────────────────────────────────────── */}
      {activeTab === 'overview' && businessId && (
        <div className="mb-6">
          <BusinessOnboardingPanel businessId={businessId} />
        </div>
      )}
      {activeTab === 'overview' && (
        <div className="rounded-3xl bg-white shadow-soft p-6">
          <h3 className="text-[15px] font-semibold text-[var(--text-primary)] mb-4">Business Details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            <DetailRow icon={Building2} label="Name" value={business.name} />
            <DetailRow icon={Hash} label="Business Code" value={business.businessCode} />
            <DetailRow icon={Info} label="Type" value={business.businessType} />
            <DetailRow icon={Info} label="Category" value={business.businessCategory} />
            <DetailRow
              icon={Info}
              label="Status"
              value={business.status?.replace(/_/g, ' ')}
            />
            <DetailRow icon={Phone} label="Phone" value={business.phone} />
            <DetailRow icon={Mail} label="Email" value={business.email} />
            <DetailRow icon={Hash} label="KRA PIN" value={business.kraPin} />
            <DetailRow
              icon={Hash}
              label="Registration Number"
              value={business.registrationNumber}
            />
            <DetailRow icon={Info} label="Tax Type" value={business.taxType} />
            <DetailRow
              icon={MapPin}
              label="Branch Count"
              value={business.branchCount != null ? String(business.branchCount) : undefined}
            />
            <DetailRow
              icon={Users}
              label="Staff Count"
              value={business.staffCount != null ? String(business.staffCount) : undefined}
            />
            <DetailRow
              icon={Calendar}
              label="Created"
              value={
                business.createdAt
                  ? new Date(business.createdAt).toLocaleString()
                  : undefined
              }
            />
          </div>
          {business.description && (
            <div className="mt-4 pt-4 border-t border-[var(--border-subtle)]">
              <p className="text-[13px] text-[var(--text-muted)] mb-1">Description</p>
              <p className="text-[14px] text-[var(--text-secondary)]">{business.description}</p>
            </div>
          )}
        </div>
      )}

      {/* ── Branches tab ────────────────────────────────────────────────── */}
      {activeTab === 'branches' && (
        <div className="rounded-3xl bg-white shadow-soft p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-[15px] font-semibold text-[var(--text-primary)] flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />
              Branches
              {Array.isArray(branches) && (
                <span className="text-[14px] font-normal text-[var(--text-muted)]">({branches.length})</span>
              )}
            </h3>
            <PermissionGate permission="branches.create">
              <button
                onClick={() => setShowCreateBranch(true)}
                className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-4 py-2 text-[13px] font-semibold text-white transition-all duration-200 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
              >
                <Plus className="w-4 h-4" strokeWidth={1.5} />
                Add Branch
              </button>
            </PermissionGate>
          </div>

          {branchesLoading && (
            <p className="text-[14px] text-[var(--text-muted)] py-4 text-center">Loading branches...</p>
          )}
          {!branchesLoading && (!Array.isArray(branches) || branches.length === 0) && (
            <p className="text-[14px] text-[var(--text-muted)] py-4 text-center">No branches yet.</p>
          )}
          {Array.isArray(branches) && branches.length > 0 && (
            <div className="space-y-3">
              {branches.map((branch: any) => (
                <div
                  key={branch.id}
                  className="flex items-center justify-between p-4 border border-[var(--border-subtle)] rounded-2xl hover:bg-[var(--bg-base)] transition-colors duration-200"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-[var(--bg-base)] text-[var(--text-muted)] rounded-2xl flex items-center justify-center">
                      <MapPin className="w-4 h-4" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="font-medium text-[var(--text-primary)] text-[14px]">{branch.name}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {branch.city && (
                          <span className="text-[13px] text-[var(--text-muted)]">{branch.city}</span>
                        )}
                        {branch.phone && (
                          <span className="text-[13px] text-[var(--text-muted)] flex items-center gap-1">
                            <Phone className="w-3 h-3" strokeWidth={1.5} />
                            {branch.phone}
                          </span>
                        )}
                        {branch.createdAt && (
                          <span className="text-[13px] text-[var(--text-muted)]">
                            {new Date(branch.createdAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <span
                    className={cn(
                      'rounded-full px-3 py-1 text-[12px] font-semibold',
                      branch.isActive ? 'bg-[var(--state-success-bg)] text-[var(--state-success)]' : 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                    )}
                  >
                    {branch.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Staff tab ───────────────────────────────────────────────────── */}
      {activeTab === 'staff' && (
        <div className="rounded-3xl bg-white shadow-soft p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-[15px] font-semibold text-[var(--text-primary)] flex items-center gap-2">
              <Users className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />
              Staff
              {Array.isArray(staff) && (
                <span className="text-[14px] font-normal text-[var(--text-muted)]">({staff.length})</span>
              )}
            </h3>
            <PermissionGate permission="staff.create">
              <button
                onClick={() => setShowAddStaff(true)}
                className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-4 py-2 text-[13px] font-semibold text-white transition-all duration-200 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
              >
                <Plus className="w-4 h-4" strokeWidth={1.5} />
                Add Staff
              </button>
            </PermissionGate>
          </div>

          {staffLoading && (
            <p className="text-[14px] text-[var(--text-muted)] py-4 text-center">Loading staff...</p>
          )}
          {!staffLoading && (!Array.isArray(staff) || staff.length === 0) && (
            <p className="text-[14px] text-[var(--text-muted)] py-4 text-center">No staff members yet.</p>
          )}
          {Array.isArray(staff) && staff.length > 0 && (
            <div className="space-y-3">
              {staff.map((s: any) => {
                const firstName = s.user?.firstName ?? '';
                const lastName = s.user?.lastName ?? '';
                const fullName = `${firstName} ${lastName}`.trim() || 'Unknown';
                return (
                  <div
                    key={s.id}
                    onClick={() => setSelectedStaff(s)}
                    className="flex items-center justify-between p-4 border border-[var(--border-subtle)] rounded-2xl hover:bg-[var(--bg-base)] transition-colors duration-200 cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-[var(--bg-elevated)] text-[var(--text-accent)] rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {initials(firstName, lastName)}
                      </div>
                      <div>
                        <div className="font-medium text-[var(--text-primary)] text-[14px]">{fullName}</div>
                        {s.user?.phone && (
                          <div className="text-[13px] text-[var(--text-muted)] mt-0.5">{s.user.phone}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {s.staffType && (
                        <span
                          className={cn(
                            'rounded-full px-3 py-1 text-[12px] font-semibold capitalize',
                            staffTypeBadge[s.staffType] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                          )}
                        >
                          {s.staffType.replace(/_/g, ' ')}
                        </span>
                      )}
                      {s.status && (
                        <span
                          className={cn(
                            'rounded-full px-3 py-1 text-[12px] font-semibold capitalize',
                            bizStatusColors[s.status] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                          )}
                        >
                          {s.status}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── KYC tab ─────────────────────────────────────────────────────── */}
      {activeTab === 'kyc' && (
        <div className="rounded-3xl bg-white shadow-soft p-6">
          <h3 className="text-[15px] font-semibold text-[var(--text-primary)] flex items-center gap-2 mb-5">
            <FileText className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />
            KYC Documents
            {Array.isArray(kycDocs) && (
              <span className="text-[14px] font-normal text-[var(--text-muted)]">({kycDocs.length})</span>
            )}
          </h3>

          {kycLoading && (
            <p className="text-[14px] text-[var(--text-muted)] py-4 text-center">Loading KYC documents...</p>
          )}
          {!kycLoading && (!Array.isArray(kycDocs) || kycDocs.length === 0) && (
            <p className="text-[14px] text-[var(--text-muted)] py-4 text-center">No KYC documents submitted.</p>
          )}
          {Array.isArray(kycDocs) && kycDocs.length > 0 && (
            <div className="space-y-3">
              {kycDocs.map((doc: any) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-4 border border-[var(--border-subtle)] rounded-2xl"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-[var(--bg-base)] text-[var(--text-muted)] rounded-2xl flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="font-medium text-[var(--text-primary)] text-[14px] capitalize">
                        {doc.documentType?.replace(/_/g, ' ') ?? 'Document'}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span
                          className={cn(
                            'rounded-full px-3 py-1 text-[12px] font-semibold capitalize',
                            kycStatusColors[doc.status] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                          )}
                        >
                          {doc.status ?? 'unknown'}
                        </span>
                        {doc.verifiedAt && (
                          <span className="text-[13px] text-[var(--text-muted)]">
                            Verified {new Date(doc.verifiedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {doc.fileUrl && (
                      <a
                        href={doc.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[12px] font-semibold rounded-full border border-[var(--border-subtle)] text-[var(--text-muted)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)] transition-all duration-200"
                      >
                        <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                        View
                      </a>
                    )}
                    {doc.status !== 'approved' && (
                      <PermissionGate permission="kyc.verify">
                        <button
                          onClick={() => kycApproveMutation.mutate(doc.id)}
                          disabled={kycApproveMutation.isPending}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-[12px] font-semibold rounded-full border border-[var(--state-success-border)] text-[var(--state-success)] bg-[var(--state-success-bg)] hover:bg-[var(--state-success-bg)] transition-all duration-200 disabled:opacity-40"
                        >
                          <CheckCircle className="w-3 h-3" strokeWidth={1.5} />
                          Approve
                        </button>
                      </PermissionGate>
                    )}
                    {doc.status !== 'rejected' && (
                      <PermissionGate permission="kyc.reject">
                        <button
                          onClick={() => setKycRejectDocId(doc.id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-[12px] font-semibold rounded-full border border-[var(--state-danger-border)] text-[var(--state-danger)] hover:bg-[var(--state-danger-bg)] transition-all duration-200"
                        >
                          <XCircle className="w-3 h-3" strokeWidth={1.5} />
                          Reject
                        </button>
                      </PermissionGate>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Finance tab ─────────────────────────────────────────────────── */}
      {activeTab === 'finance' && (
        <div className="space-y-4">
          {businessId && <BusinessPayoutPanel businessId={businessId} />}
          <div className="rounded-3xl bg-white shadow-soft p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <h3 className="text-[15px] font-semibold text-[var(--text-primary)] flex items-center gap-2">
                <Wallet className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />
                Business Wallets
              </h3>
              {/* Full treasury view: holds, withdrawals, missing wallets and the
                  operator tools to move money on any of them. */}
              <Link
                to={`/finance/owners/business/${businessId}`}
                className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--text-accent)] hover:underline"
              >
                <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
                Open owner ledger
              </Link>
            </div>
            {walletsLoading && (
              <p className="text-[14px] text-[var(--text-muted)] py-4 text-center">Loading wallets...</p>
            )}
            {!walletsLoading && wallets.length === 0 && (
              <p className="text-[14px] text-[var(--text-muted)] py-4 text-center">No wallets found.</p>
            )}
            {wallets.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {wallets.map((w: any) => (
                  <div
                    key={w.walletId}
                    className="border border-[var(--border-subtle)] rounded-2xl p-4 bg-[var(--bg-base)]"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
                          {w.walletTypeCode?.replace(/_/g, ' ')}
                        </p>
                        {w.walletTypeName && (
                          <p className="text-[13px] text-[var(--text-muted)]">{w.walletTypeName}</p>
                        )}
                      </div>
                      <TrendingUp className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between items-baseline">
                        <span className="text-[13px] text-[var(--text-muted)]">Available</span>
                        <span className="font-display text-[28px] font-medium tracking-[-0.03em] text-[var(--text-primary)] leading-none">
                          {formatKes(w.availableBalance ?? 0, w.currency)}
                        </span>
                      </div>
                      <div className="flex justify-between items-baseline text-[13px] text-[var(--text-muted)]">
                        <span>Balance</span>
                        <span>{formatKes(w.balance ?? 0, w.currency)}</span>
                      </div>
                      {(w.heldBalance ?? 0) > 0 && (
                        <div className="flex justify-between items-baseline text-[13px] text-[var(--state-warning)]">
                          <span>On hold</span>
                          <span>{formatKes(w.heldBalance, w.currency)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Products tab ────────────────────────────────────────────────── */}
      {activeTab === 'products' && (
        <div className="rounded-3xl bg-white shadow-soft p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-[15px] font-semibold text-[var(--text-primary)] flex items-center gap-2">
              <Package className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />
              Products
              {productsTotal > 0 && (
                <span className="text-[14px] font-normal text-[var(--text-muted)]">({productsTotal.toLocaleString()})</span>
              )}
            </h3>
            <input
              type="search"
              value={productSearchInput}
              onChange={(e) => setProductSearchInput(e.target.value)}
              placeholder="Search products by name"
              className="w-64 rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-base)] px-3 py-2 text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none"
            />
          </div>
          {productActionError && (
            <div className="mb-4 rounded-2xl border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] px-4 py-3 flex items-start justify-between gap-3">
              <p className="text-[13px] text-[var(--state-danger)]">{productActionError}</p>
              <button
                type="button"
                onClick={() => setProductActionError(null)}
                className="text-[12px] font-semibold text-[var(--state-danger)] hover:underline"
              >
                Dismiss
              </button>
            </div>
          )}
          {productsLoading && (
            <p className="text-[14px] text-[var(--text-muted)] py-4 text-center">Loading products...</p>
          )}
          {!productsLoading && products.length === 0 && (
            <p className="text-[14px] text-[var(--text-muted)] py-4 text-center">
              {productSearch ? `No products match "${productSearch}".` : 'No products for this business.'}
            </p>
          )}
          {products.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[var(--bg-base)] border-b border-[var(--border-subtle)]">
                  <tr>
                    <th className="text-left py-2.5 pr-4 pl-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">Product</th>
                    <th className="text-left py-2.5 pr-4 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">Type</th>
                    <th className="text-right py-2.5 pr-4 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">Price</th>
                    <th className="text-right py-2.5 pr-4 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">Stock</th>
                    <th className="text-center py-2.5 pr-4 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">Status</th>
                    <th className="text-right py-2.5 pr-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-subtle)]">
                  {(products as any[]).map((p) => (
                    <tr
                      key={p.id}
                      onClick={() => setSelectedProduct(p)}
                      className="hover:bg-[var(--bg-base)] transition-colors cursor-pointer"
                    >
                      <td className="py-3 pr-4 pl-3">
                        <div className="flex items-center gap-3">
                          {p.primaryImage ? (
                            <img
                              src={p.primaryImage}
                              alt={p.name}
                              className="w-10 h-10 rounded-xl object-cover bg-[var(--bg-base)] flex-shrink-0"
                              onError={(e) => {
                                (e.currentTarget as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-xl bg-[var(--bg-base)] flex items-center justify-center flex-shrink-0">
                              <Package className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="font-medium text-[var(--text-primary)] truncate">{p.name}</div>
                            {p.sku && <div className="text-[13px] text-[var(--text-muted)] mt-0.5">SKU: {p.sku}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-[14px] text-[var(--text-secondary)] capitalize">{p.productType}</td>
                      <td className="py-3 pr-4 text-right text-[14px] text-[var(--text-primary)] font-medium">
                        {formatKes(p.basePrice ?? 0)}
                      </td>
                      <td className="py-3 pr-4 text-right text-[14px] font-medium">
                        {typeof p.totalStock === 'number' ? (
                          <span className={cn(
                            p.totalStock === 0 ? 'text-[var(--state-danger)]' : p.totalStock < 10 ? 'text-[var(--state-warning)]' : 'text-[var(--text-primary)]',
                          )}>
                            {p.totalStock.toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-[var(--text-muted)]">—</span>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-center">
                        <span className={cn(
                          'rounded-full px-3 py-1 text-[12px] font-semibold',
                          p.isActive ? 'bg-[var(--state-success-bg)] text-[var(--state-success)]' : 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                        )}>
                          {p.isActive ? 'Active' : 'Suspended'}
                        </span>
                      </td>
                      <td className="py-3 pr-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <PermissionGate permission="products.update">
                            {p.isActive ? (
                              <button
                                onClick={() => suspendProductMutation.mutate(p.id)}
                                disabled={suspendProductMutation.isPending}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-[12px] font-semibold rounded-full border border-[var(--state-warning-border)] text-[var(--state-warning)] hover:bg-[var(--state-warning-bg)] transition-all duration-200 disabled:opacity-40"
                              >
                                <PauseCircle className="w-3 h-3" strokeWidth={1.5} />
                                Suspend
                              </button>
                            ) : (
                              <button
                                onClick={() => activateProductMutation.mutate(p.id)}
                                disabled={activateProductMutation.isPending}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-[12px] font-semibold rounded-full border border-[var(--state-success-border)] text-[var(--state-success)] bg-[var(--state-success-bg)] hover:bg-[var(--state-success-bg)] transition-all duration-200 disabled:opacity-40"
                              >
                                <PlayCircle className="w-3 h-3" strokeWidth={1.5} />
                                Activate
                              </button>
                            )}
                          </PermissionGate>
                          <PermissionGate permission="products.delete">
                            <button
                              onClick={() => {
                                if (confirm(`Remove "${p.name}" from this business?`)) {
                                  removeProductMutation.mutate(p.id);
                                }
                              }}
                              disabled={removeProductMutation.isPending}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-[12px] font-semibold rounded-full border border-[var(--state-danger-border)] text-[var(--state-danger)] hover:bg-[var(--state-danger-bg)] transition-all duration-200 disabled:opacity-40"
                            >
                              <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                              Remove
                            </button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {productsTotal > PRODUCT_PAGE_SIZE && (
            <div className="mt-4 flex items-center justify-between text-[13px] text-[var(--text-muted)]">
              <span>
                {((productPage - 1) * PRODUCT_PAGE_SIZE + 1).toLocaleString()}-
                {Math.min(productPage * PRODUCT_PAGE_SIZE, productsTotal).toLocaleString()} of {productsTotal.toLocaleString()}
                {productsFetching && ' · updating...'}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={productPage <= 1}
                  onClick={() => setProductPage((n) => Math.max(1, n - 1))}
                  className="rounded-xl border border-[var(--border-subtle)] px-3 py-1.5 font-medium text-[var(--text-primary)] disabled:opacity-40"
                >
                  Previous
                </button>
                <span>Page {productPage} of {productPageCount}</span>
                <button
                  type="button"
                  disabled={productPage >= productPageCount}
                  onClick={() => setProductPage((n) => Math.min(productPageCount, n + 1))}
                  className="rounded-xl border border-[var(--border-subtle)] px-3 py-1.5 font-medium text-[var(--text-primary)] disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Modals ──────────────────────────────────────────────────────── */}

      {rejectModal && (
        <ReasonModal
          title="Reject Business"
          description={`Provide a reason for rejecting "${business.name}".`}
          confirmLabel="Confirm Rejection"
          confirmClass="bg-[var(--state-danger)] hover:bg-[var(--state-danger)]"
          onConfirm={(reason) => rejectMutation.mutate(reason)}
          onCancel={() => setRejectModal(false)}
          isPending={rejectMutation.isPending}
        />
      )}

      {editModal && business && (
        <BusinessEditModal
          business={business}
          onClose={() => setEditModal(false)}
          onSaved={() => { setEditModal(false); invalidateBiz(); }}
        />
      )}

      {suspendModal && (
        <ReasonModal
          title="Suspend Business"
          description={`Provide a reason for suspending "${business.name}".`}
          confirmLabel="Confirm Suspension"
          confirmClass="bg-[var(--state-warning)] hover:bg-[var(--state-warning)]"
          onConfirm={(reason) => suspendMutation.mutate(reason)}
          onCancel={() => setSuspendModal(false)}
          isPending={suspendMutation.isPending}
        />
      )}

      {showCreateBranch && businessId && (
        <CreateBranchModal businessId={businessId} onClose={() => setShowCreateBranch(false)} />
      )}

      {showAddStaff && businessId && (
        <AddStaffModal businessId={businessId} onClose={() => setShowAddStaff(false)} />
      )}

      {kycRejectDocId && (
        <ReasonModal
          title="Reject KYC Document"
          description="Provide a reason for rejecting this document."
          confirmLabel="Confirm Rejection"
          confirmClass="bg-[var(--state-danger)] hover:bg-[var(--state-danger)]"
          onConfirm={(reason) => kycRejectMutation.mutate({ docId: kycRejectDocId, reason })}
          onCancel={() => setKycRejectDocId(null)}
          isPending={kycRejectMutation.isPending}
        />
      )}

      {selectedProduct && businessId && (
        <ProductDetailModal
          businessId={businessId}
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}

      {selectedStaff && businessId && (
        <StaffDetailModal
          businessId={businessId}
          staff={selectedStaff}
          onClose={() => setSelectedStaff(null)}
        />
      )}
    </div>
  );
};
